import styled from '@emotion/styled';
import { Avatar, Badge, color, Icon, Popover } from '@uniquegood/realworld-studio-design';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import whiteLogoImage from '@/assets/images/new_logo_studio_white.png';
import purpleLogoImage from '@/assets/images/new_logo_studio_purple.png';
import whiteSmallLogoImage from '@/assets/images/new_logo_studio_white.svg';
import purpleSmallLogoImage from '@/assets/images/new_logo_studio_purple.svg';
import { debounceFn } from '@/utils';
import landingData from '@/pages/landing/data/landing';
import { INavigationItem } from '@/pages/landing/types/interface';
import { useIsMobile, useRequest } from '@/hooks';
import { ClickEventName, PageViewEventName, track } from '@/track';
import { onMobile } from '@/styles/responsive';
import UserMenu from '../TopBar/components/UserMenu';
import { useEventListener } from '../ActionHandler/hooks';

const SharedNavBar = () => {
  const history = useHistory();
  const location = useLocation();
  const [scrollY, setScrollY] = useState(0);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const isLandingPage = location.pathname === '/';
  const isScrolled = scrollY > 50;
  const isPricingPage = location.pathname === '/pricing';

  const handleScroll = () => {
    setScrollY(window.scrollY);
  };

  const handleResize = debounceFn(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, 1000);

  const getLogo = () => {
    if (windowSize.width < 768) {
      return isLandingPage && !isScrolled ? whiteSmallLogoImage : purpleSmallLogoImage;
    }
    return isLandingPage && !isScrolled ? whiteLogoImage : purpleLogoImage;
  };

  const handleNavigate = (item: INavigationItem) => {
    if (item.type === 'scroll') {
      track.onClick({ clickEventName: ClickEventName.click_landing_service_info });
      if (location.pathname !== '/') {
        history.push('/');
        setTimeout(() => {
          const element = document.getElementById(item.target);
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        track.onPageView({ pageViewEventName: PageViewEventName.view_landing_service_info });
      } else {
        const element = document.getElementById(item.target);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        track.onPageView({ pageViewEventName: PageViewEventName.view_landing_service_info });
      }
    } else if (item.type === 'internal') {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan
      });
      history.push(item.target);
    } else if (item.type === 'external') {
      track.onClick({ clickEventName: ClickEventName.click_landing_help });
      window.open(item.target, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const backgroundColor =
    (isLandingPage || isPricingPage) && !isScrolled ? 'transparent' : color.white;
  const textColor = isLandingPage && !isScrolled ? color.white : color.black;

  const accessToken = localStorage.getItem('accessToken');

  const { data: me } = accessToken ? useRequest<MyInfo>('/api/me') : { data: undefined };

  const [showUserMenu, setShowUserMenu] = useState(false);
  const toggleShowUserMenu = useCallback(() => {
    setShowUserMenu((showUserMenu) => !showUserMenu);
  }, []);

  const isMobile = useIsMobile();

  useEventListener('click', () => setShowUserMenu(false));

  return (
    <NavBarContainer isScrollDown={isScrolled} backgroundColor={backgroundColor}>
      <nav className="navbar-contents" role="navigation" aria-label="메인 네비게이션">
        <LogoLink
          onClick={() => {
            if (isScrolled) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              history.push('/');
            }
          }}
        >
          <img
            className={windowSize.width < 768 ? undefined : 'logo'}
            src={getLogo()}
            alt="realworld studio logo"
          />
          <Badge type="ai">AI Beta</Badge>
        </LogoLink>
        <NavLinks>
          {landingData.navigation.items.map((item) => (
            <NavLinkItem key={item.id} onClick={() => handleNavigate(item)} textColor={textColor}>
              {item.label}
            </NavLinkItem>
          ))}
        </NavLinks>
        <ButtonContainer>
          {accessToken ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 10px;
              `}
            >
              <CtaButton href="/apps">
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  `}
                >
                  <Icon icon="home_solid" color={color.white} />
                  채널 홈
                </div>
              </CtaButton>
              <Popover
                active={showUserMenu}
                activator={
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleShowUserMenu();
                    }}
                    css={[AvatarStyle]}
                  >
                    <Avatar source={me?.profileImage} size={isMobile ? 'small' : 'medium'} />
                  </button>
                }
                onClose={() => {
                  // 팝오버를 열때에도 호출이 되어서 빈 함수로 둠
                  // setShowUserMenu(false);
                }}
              >
                <UserMenu onClose={toggleShowUserMenu} cssStyle={UserMenuLayout} />
              </Popover>
            </div>
          ) : (
            <CtaButton
              href="/auth/login"
              onClick={() =>
                track.onClick({ clickEventName: ClickEventName.click_landing_studio_start })
              }
            >
              스튜디오 시작하기
            </CtaButton>
          )}
        </ButtonContainer>
      </nav>
    </NavBarContainer>
  );
};

export default SharedNavBar;

const NavBarContainer = styled.div<{ isScrollDown: boolean; backgroundColor: string }>`
  position: fixed;
  width: 100%;
  height: 64px;
  background-color: ${(props) => props.backgroundColor};
  z-index: 1000;
  transition: background-color 0.3s;

  .navbar-contents {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 40px;
    margin: 0 auto;
    position: relative;
  }

  @media (max-width: 960px) {
    .navbar-contents {
      padding: 0 26px;
    }
  }

  .logo {
    width: 219px;
    height: 16px;
  }
`;

const LogoLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const NavLinks = styled.ul`
  display: flex;
  gap: 40px;
  margin: 0;
  padding: 0;
  list-style: none;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 960px) {
    display: none;
  }
`;

const NavLinkItem = styled.li<{ textColor: string }>`
  font-size: 15px;
  font-weight: 500;
  color: ${(props) => props.textColor};
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const CtaButton = styled.a`
  border-radius: 8px;
  padding: 6px 12px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  color: ${color.white};
  background-color: ${color.palette_primary_purple_100};
  text-decoration: none;
  display: inline-block;

  &:hover {
    opacity: 0.9;
  }
`;

const UserMenuLayout = css`
  box-shadow: 0px 2px 30px rgba(0, 0, 0, 0.2);
  width: 393px;

  ${onMobile} {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background-color: ${color.surface_default_default};
    width: 100%;
    height: 100%;

    && {
      box-shadow: none;
    }
  }
`;

const AvatarStyle = css`
  border: none;
  background: none;
  cursor: pointer;
  vertical-align: middle;
`;
